import { AsyncPipe, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Params, Router, RouterOutlet } from '@angular/router';
import { LoaderModule } from '@x/common/loader';
import {
  NavigationEvent,
  TENavigationEnd,
  TENavigationStart,
} from '@x/common/tracking/event-models/navigation.events';
import { TrackingService } from '@x/common/tracking/tracking.service';
import { ChannelContextService } from '@x/ecommerce-shop/app/channel/services/channel-context.service';
import { RoutingContextService } from '@x/ecommerce-shop/app/core/routing/routing-context.service';
import { Subscription } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';

@Component({
  selector: 'x-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, LoaderModule, NgIf, AsyncPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription$: Subscription = new Subscription();
  hideLoader$ = this.rc.routeTransitionEnd$.pipe(take(1));

  constructor(
    private router: Router,
    @Inject(LOCALE_ID) private locale: string,
    private channelContextService: ChannelContextService,
    private trackingService: TrackingService,
    private rc: RoutingContextService,
  ) {}

  ngOnInit(): void {
    this.sendViewPageEvent();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private sendViewPageEvent() {
    this.subscription$.add(
      this.router.events
        .pipe(
          filter(
            (event: any) => event instanceof NavigationEnd || event instanceof NavigationStart,
          ),
          debounceTime(50),
          map((event: NavigationEnd | NavigationStart): NavigationEvent | undefined => {
            const path = event.url;
            const channel = this.channelContextService.channelCode ?? undefined;
            const locale = this.locale;
            const routeOrigin = this.trackingService.routeOrigin;
            const queryParams = this.router.parseUrl(path).queryParams;
            const utmParams: Params = Object.keys(queryParams)
              .filter((key) => key.startsWith('utm_'))
              .reduce((obj, key) => {
                obj[key] = queryParams[key];
                return obj;
              }, {} as Params);

            if (!channel) return;

            if (event instanceof NavigationStart) {
              return new TENavigationStart({
                routeOrigin,
                channel,
                locale,
                path,
              });
            }

            return new TENavigationEnd({
              routeOrigin,
              channel,
              locale,
              path,
              utmParams,
            });
          }),
        )
        .subscribe((event) => {
          if (event) this.trackingService.sendEvent(event);
        }),
    );
  }
}
